import axios, { AxiosError } from 'axios';

import {
  IResponse,
  IWatchlistItems,
  IWatchlistPerformers,
  IWatchlistWallet,
} from '..';
import { Tag } from '../enums';

type UpdateWatchlistItemPayload =
  | {
      tag: Tag;
    }
  | {
      old: number;
      new: number;
    }
  | {
      tag: Tag;
      old: number;
      new: number;
    };

export interface IWatchListApi {
  watchlistEndpoint: string;
  walletWatchlistEndpoint: string;

  get: (
    network: string,
    wallet: string,
    authToken: string
  ) => Promise<IResponse<IWatchlistItems>>;
  addPair: (
    network: string,
    wallet: string,
    pairId: number,
    payload: { tag?: Tag },
    authToken: string
  ) => Promise<any>;
  remove: (
    network: string,
    wallet: string,
    pairId: number,
    authToken: string
  ) => Promise<any>;
  updatePair: (
    network: string,
    wallet: string,
    pairId: number,
    payload: UpdateWatchlistItemPayload,
    authToken: string
  ) => Promise<any>;
  gainers: () => Promise<IResponse<IWatchlistPerformers>>;
  tvl: () => Promise<IResponse<IWatchlistPerformers>>;
  getWallets: (
    network: string,
    address: string,
    authToken: string,
    controller?: AbortController
  ) => Promise<IWatchlistWallet[] | null>;
  addWallet: (
    network: string,
    address: string,
    id: number,
    payload: { tag?: Tag },
    authToken: string
  ) => Promise<{
    success: boolean;
    error?: AxiosError;
  }>;
  addWalletByAddrChain: (
    network: string,
    address: string,
    walletNetwork: string,
    walletAddress: string,
    payload: { tag?: Tag },
    authToken: string
  ) => Promise<{
    success: boolean;
    error?: AxiosError;
  }>;
  updateWallet: (
    network: string,
    address: string,
    id: number,
    payload: UpdateWatchlistItemPayload,
    authToken: string
  ) => Promise<{
    success: boolean;
    error?: AxiosError;
  }>;
  removeWallet: (
    network: string,
    address: string,
    id: number,
    authToken: string
  ) => Promise<{
    success: boolean;
    error?: AxiosError;
  }>;
}

export class WatchListApi implements IWatchListApi {
  watchlistEndpoint: string;
  walletWatchlistEndpoint: string;

  constructor(apiGateway: string) {
    if (!apiGateway) {
      apiGateway = 'https://api.kattana.trade';
    }

    this.watchlistEndpoint = `${apiGateway}/watchlist`;
    this.walletWatchlistEndpoint = `${apiGateway}/watchlist/wallets`;
  }

  public get = async (
    network: string,
    wallet: string,
    authToken: string
  ): Promise<IResponse<IWatchlistItems>> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
      };
      const res = await axios.get(
        `${this.watchlistEndpoint}/${network}/${wallet}`,
        config
      );

      return {
        success: true,
        data: res.data,
      };
    } catch (err) {
      console.warn(err);

      if (axios.isAxiosError(err)) {
        return {
          success: false,
          error: err,
        };
      }

      return {
        success: false,
      };
    }
  };

  public addPair = async (
    network: string,
    wallet: string,
    pairId: number,
    payload: { tag?: Tag },
    authToken: string
  ): Promise<any> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
        params: payload,
      };
      const res = await axios.post(
        `${this.watchlistEndpoint}/${network}/${wallet}/${pairId}`,
        {},
        config
      );

      return {
        success: true,
        data: res.data,
      };
    } catch (err) {
      console.warn(err);

      if (axios.isAxiosError(err)) {
        return {
          success: false,
          error: err,
        };
      }

      return {
        success: false,
      };
    }
  };

  public updatePair = async (
    network: string,
    wallet: string,
    pairId: number,
    payload: UpdateWatchlistItemPayload,
    authToken: string
  ): Promise<any> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
        params: payload,
      };
      const res = await axios.put(
        `${this.watchlistEndpoint}/${network}/${wallet}/${pairId}`,
        {},
        config
      );

      return {
        success: true,
        data: res.data,
      };
    } catch (err) {
      console.warn(err);

      if (axios.isAxiosError(err)) {
        return {
          success: false,
          error: err,
        };
      }

      return {
        success: false,
      };
    }
  };

  public remove = async (
    network: string,
    wallet: string,
    pairId: number,
    authToken: string
  ): Promise<any> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
      };
      const res = await axios.delete(
        `${this.watchlistEndpoint}/${network}/${wallet}/${pairId}`,
        config
      );

      return {
        success: true,
        data: res.data,
      };
    } catch (err) {
      console.warn(err);

      if (axios.isAxiosError(err)) {
        return {
          success: false,
          error: err,
        };
      }

      return {
        success: false,
      };
    }
  };

  public gainers = async (): Promise<IResponse<IWatchlistPerformers>> => {
    try {
      const res = await axios.get(`${this.watchlistEndpoint}/gainers`);

      return {
        success: true,
        data: res.data,
      };
    } catch (err) {
      console.warn(err);

      if (axios.isAxiosError(err)) {
        return {
          success: false,
          error: err,
        };
      }

      return {
        success: false,
      };
    }
  };

  public tvl = async (): Promise<IResponse<IWatchlistPerformers>> => {
    try {
      const res = await axios.get(`${this.watchlistEndpoint}/tvl`);

      return {
        success: true,
        data: res.data,
      };
    } catch (err) {
      console.warn(err);

      if (axios.isAxiosError(err)) {
        return {
          success: false,
          error: err,
        };
      }

      return {
        success: false,
      };
    }
  };

  public getWallets = async (
    network: string,
    address: string,
    authToken: string,
    controller?: AbortController
  ): Promise<IWatchlistWallet[] | null> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
        signal: controller?.signal,
      };
      const url = `${this.walletWatchlistEndpoint}/${network}/${address}`;

      const { data } = await axios.get(url, config);
      return data;
    } catch (error) {
      console.warn(error);
      return null;
    }
  };

  public addWallet = async (
    network: string,
    address: string,
    id: number,
    payload: { tag?: Tag },
    authToken: string
  ): Promise<{
    success: boolean;
    error?: AxiosError;
  }> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
        params: payload,
      };
      const url = `${this.walletWatchlistEndpoint}/${network}/${address}/${id}`;

      await axios.post(url, {}, config);
      return { success: true };
    } catch (err) {
      console.warn(err);
      if (axios.isAxiosError(err)) {
        return {
          success: false,
          error: err,
        };
      }
      return { success: false };
    }
  };
  public addWalletByAddrChain = async (
    network: string,
    address: string,
    walletNetwork: string,
    walletAddress: string,
    payload: { tag?: Tag },
    authToken: string
  ): Promise<{
    success: boolean;
    id?: number;
    error?: AxiosError;
  }> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
        params: payload,
      };
      const url = `${this.walletWatchlistEndpoint}/${network}/${address}/noid/${walletNetwork}/${walletAddress}`;

      const { data } = await axios.post(url, {}, config);

      if (data.status === 'ok') {
        return { success: true, id: data.id };
      }
      return { success: false };
    } catch (err) {
      console.warn(err);
      if (axios.isAxiosError(err)) {
        return {
          success: false,
          error: err,
        };
      }
      return { success: false };
    }
  };

  public updateWallet = async (
    network: string,
    address: string,
    id: number,
    payload: UpdateWatchlistItemPayload,
    authToken: string
  ): Promise<{
    success: boolean;
    error?: AxiosError;
  }> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
        params: payload,
      };
      const url = `${this.walletWatchlistEndpoint}/${network}/${address}/${id}`;

      await axios.put(url, {}, config);
      return { success: true };
    } catch (err) {
      console.warn(err);
      if (axios.isAxiosError(err)) {
        return {
          success: false,
          error: err,
        };
      }
      return { success: false };
    }
  };

  public removeWallet = async (
    network: string,
    address: string,
    id: number,
    authToken: string
  ): Promise<{
    success: boolean;
    error?: AxiosError;
  }> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
      };
      const url = `${this.walletWatchlistEndpoint}/${network}/${address}/${id}`;

      await axios.delete(url, config);
      return { success: true };
    } catch (err) {
      console.warn(err);
      if (axios.isAxiosError(err)) {
        return {
          success: false,
          error: err,
        };
      }
      return { success: false };
    }
  };
}
